<template>
  <div class="audience-builder">
    <div v-if="!rows.length" class="audience-builder__empty">
      No filters yet &mdash; this email would go to every creator who can receive marketing email.
    </div>

    <!--
      is-empty hides the operator and the value until a field is chosen: an
      empty operator box next to a bare "Choose a filter first" reads as broken
      rather than as the next step.
    -->
    <div
      v-for="(row, index) in rows"
      :key="row.key"
      class="audience-builder__row"
      :class="{ 'is-empty': !row.field, 'is-unfinished': showUnfinished && isUnfinished(row) }"
      :data-unfinished="showUnfinished && isUnfinished(row) ? '' : null"
    >
      <span class="audience-builder__conjunction">{{ index === 0 ? 'Where' : 'And' }}</span>

      <!--
        A saved filter naming a field this build no longer has. It is shown, not
        silently dropped: dropping it would widen the audience with nothing on
        screen saying so, and guardStep refuses the step while one is present.
      -->
      <p v-if="row.unknown" class="audience-builder__unknown">
        This campaign was saved with a filter this page no longer offers
        (<code>{{ row.unknown }}</code>). Remove it to continue &mdash; the audience
        cannot be counted or sent while it is here.
      </p>

      <!-- 1. field -->
      <select
        v-if="!row.unknown"
        class="audience-builder__field"
        :aria-label="`Filter ${index + 1}: field`"
        :value="row.field"
        @change="changeField(row, $event.target.value)"
      >
        <option value="" disabled>Choose a filter&hellip;</option>
        <!--
          The group name is repeated inside each option on purpose. macOS draws
          select popups itself and ignores CSS on optgroup, rendering the group
          headings at very low contrast - unreadable under a dark system theme.
          Options are drawn at full contrast, so that is where the grouping has
          to live to survive every platform. The optgroup stays for the browsers
          that do render it and for screen readers.
        -->
        <optgroup v-for="group in groupedFields" :key="group.name" :label="group.name">
          <option v-for="field in group.fields" :key="field.key" :value="field.key">
            {{ group.name }} &middot; {{ field.label }}
          </option>
        </optgroup>
      </select>

      <!-- 2. operator -->
      <select
        v-if="!row.unknown"
        v-model="row.operator"
        class="audience-builder__operator"
        :aria-label="controlLabel(row, index, 'Condition')"
        :disabled="!row.field"
      >
        <option v-for="operator in operatorsFor(row)" :key="operator" :value="operator">
          {{ operatorLabel(operator, row) }}
        </option>
      </select>

      <!-- 3. value -->
      <div v-if="!row.unknown" class="audience-builder__value">
        <!--
          The one option list with no ceiling: a client's campaigns. It is the
          same picker the message step links a campaign with, set to many.
        -->
        <campaign-picker
          v-if="sourceOf(row) === 'campaigns'"
          multiple
          :url="campaignsUrl"
          v-model="row.value"
          placeholder="Choose campaigns"
          label="Choose campaigns for this filter"
        />

        <template v-else-if="typeOf(row) === 'enum'">
          <div
            class="audience-builder__options"
            :class="{ 'is-list': isSearchable(row) }"
            role="group"
            :aria-label="controlLabel(row, index, 'Value')"
          >
            <input
              v-if="isSearchable(row)"
              type="search"
              class="audience-builder__option-search"
              :aria-label="`Search ${fieldOf(row).label.toLowerCase()}`"
              v-model="row.optionQuery"
              :placeholder="`Search ${fieldOf(row).label.toLowerCase()}…`"
            >
            <label v-for="option in visibleOptions(row)" :key="option.value" class="audience-builder__option">
              <!--
                The native box is hidden rather than removed: it stays the thing
                that is focused, checked and read out, and the span beside it is
                only its picture. Same technique as the platform picker in the
                campaign wizard.
              -->
              <input type="checkbox" :value="option.value" v-model="row.value">
              <!-- Only the list draws a box; a chip shows its state by filling. -->
              <span v-if="isSearchable(row)" class="marketing-check" aria-hidden="true"><i class="fa-solid fa-check"></i></span>
              <i v-if="option.icon" :class="option.icon" aria-hidden="true"></i>
              <span class="audience-builder__option-label">{{ option.label }}</span>
            </label>
            <p v-if="!visibleOptions(row).length" class="audience-builder__hint">Nothing matches that.</p>
          </div>
          <span v-if="isSearchable(row) && row.value.length" class="audience-builder__selected">
            {{ row.value.length }} selected
          </span>
        </template>

        <input
          v-else-if="typeOf(row) === 'number'"
          type="number"
          :aria-label="controlLabel(row, index, 'Value')"
          v-model.number="row.value"
          placeholder="0"
        >

        <input
          v-else-if="typeOf(row) === 'date'"
          type="date"
          :aria-label="controlLabel(row, index, 'Value')"
          v-model="row.value"
        >

        <select
          v-else-if="typeOf(row) === 'bool'"
          :aria-label="controlLabel(row, index, 'Value')"
          v-model="row.value"
        >
          <option :value="true">Yes</option>
          <option :value="false">No</option>
        </select>

        <input
          v-else-if="typeOf(row) === 'text'"
          type="text"
          :aria-label="controlLabel(row, index, 'Value')"
          v-model="row.value"
          :placeholder="row.operator === 'in' ? 'Texas, California' : 'Texas'"
        >

        <span v-else class="audience-builder__hint">Choose a filter first</span>
      </div>

      <button type="button" class="audience-builder__remove" :aria-label="controlLabel(row, index, 'Remove')" @click="removeRow(index)">
        <i class="fa-solid fa-trash-can"></i>
      </button>

      <p v-if="showUnfinished && isUnfinished(row)" class="audience-builder__unfinished">
        Choose a value for this filter, or remove it.
      </p>
      <p v-else-if="fieldOf(row) && fieldOf(row).help" class="audience-builder__help">{{ fieldOf(row).help }}</p>
    </div>

    <div class="audience-builder__footer">
      <button type="button" class="audience-builder__add" @click="addRow">
        <i class="fa-solid fa-plus"></i> Add filter
      </button>

      <div class="audience-builder__estimate" :class="{ 'is-loading': isCounting, 'is-error': !!error }">
        <template v-if="blocked">Not counted &mdash; see the filter above</template>
        <template v-else-if="error">{{ error }}</template>
        <template v-else-if="isCounting">Counting&hellip;</template>
        <template v-else-if="recipients !== null">
          <strong>{{ recipients.toLocaleString() }}</strong>
          {{ recipients === 1 ? 'creator' : 'creators' }} will receive this
        </template>
      </div>
    </div>

    <input type="hidden" :name="inputName" :value="payload">

    <!--
      The creators this campaign would reach, so the client can narrow further
      by hand. Shown with no filters too: "everybody who can receive marketing
      email" is a real audience, and it is the one the step starts on.

      The selection stays here rather than inside the picker: it is part of what
      the step submits, it outlives any reload of that list, and the estimate
      above is computed from it.
    -->
    <creator-picker
      v-if="!blocked"
      :url="creatorsUrl"
      :filters="complete"
      :audience-count="count"
      v-model="selected"
    />

    <!--
      One field holding the whole list as JSON, the way the filters above are
      submitted - not one input per id. PHP caps a POST at max_input_vars fields
      (1000 by default) and truncates past it without an error, so a client who
      ticked 1200 creators would have mailed some other 1000.

      Empty means "everyone the filters match", and is still submitted: the step
      has to be able to clear a selection saved earlier.
    -->
    <input type="hidden" name="selected_creator_ids" :value="JSON.stringify(selected)">
  </div>
</template>

<script>
/**
 * The audience step of the client's email campaign builder: rows of
 * [field] [operator] [value], plus a live estimate of how many creators match.
 *
 * The field registry is not defined here - it is rendered from
 * App\Services\Marketing\AudienceSegment::fields(), which is also what the
 * request validates against and what builds the query at send time. That is
 * what keeps the three in step: an operator this component offers is by
 * construction one the server accepts, and the number shown here is produced by
 * the same query that later picks the recipients.
 */

import CampaignPicker from './CampaignPicker.vue'
import CreatorPicker from './CreatorPicker.vue'
import { LIST_DEBOUNCE, requestJson } from './paged-list.mjs'

/**
 * One filter row, built from a saved filter or from nothing.
 *
 * A module function rather than a method, so `data()` can build the saved rows
 * before the first render. That matters: rows added afterwards look like the
 * client rewriting the audience, and the watcher below answers that by
 * clearing the selection - which, on a reopened draft, would drop the very
 * picks the draft was saved with.
 *
 * @param {Object} fields  The field registry.
 * @param {?Object} filter A saved filter, or null for an empty row.
 * @param {number} key     Render key; only has to be unique within the step.
 * @return {Object}
 */
function rowFrom(fields, filter, key) {
  const known = filter && filter.field ? fields[filter.field] : null

  return {
    key,
    // Set when a saved filter names a field this build does not have. The row
    // then renders as a refusal and blocks the step, because the alternative -
    // dropping it - widens the audience with nothing on screen saying so.
    unknown: filter && filter.field && !known ? String(filter.field) : '',
    field: known ? filter.field : '',
    operator: known ? filter.operator : '',
    value: known ? filter.value : '',
    optionQuery: ''
  }
}

export default {
  name: 'AudienceBuilder',

  components: { CampaignPicker, CreatorPicker },

  props: {
    // Field registry as fields() returns it, keyed by field key.
    fields: {
      type: Object,
      required: true
    },
    // Previously saved filters, when editing a draft.
    initial: {
      type: Array,
      required: false,
      default: () => []
    },
    // Endpoint returning { count } for a set of filters.
    countUrl: {
      type: String,
      required: true
    },
    // Name of the hidden input the filters are submitted under.
    inputName: {
      type: String,
      required: false,
      default: 'audience_filters'
    },
    // Endpoint returning one page of the creators the filters match.
    creatorsUrl: {
      type: String,
      required: true
    },
    // Endpoint listing the client's own campaigns, for the one filter whose
    // options have no ceiling.
    campaignsUrl: {
      type: String,
      required: true
    },
    // Creators picked on a previous visit to this step.
    initialSelected: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data() {
    // Built here rather than in a hook so that `complete` is already right at
    // the first render: the child list asks the server once, for the audience
    // the draft was saved with, instead of once for "everyone" and again a
    // beat later.
    const rows = this.initial.map((filter, index) => rowFrom(this.fields, filter, index + 1))

    return {
      rows,
      nextKey: rows.length + 1,
      selected: [...this.initialSelected],

      count: null,
      isCounting: false,
      error: '',
      countTimer: null,
      // The count still in flight, so a newer one can cancel it.
      countRequest: null,
      // Raised the first time the step is refused, so a row the client is
      // still filling in is not scolded while they are typing.
      showUnfinished: false,
      form: null
    }
  },

  computed: {
    /**
     * Fields in the registry's own order, split into the groups it declares,
     * so the select mirrors how a creator profile is actually organised.
     */
    groupedFields() {
      const groups = []

      Object.values(this.fields)
        // A choice list with nothing in it cannot narrow anything - a platform
        // none are connected to, say. Hiding it is presentation only: the
        // server still accepts the field, so nothing offered here can be
        // refused there. A field whose options come from an endpoint is never
        // empty in this sense: nothing has been asked for yet.
        .filter((field) => field.type !== 'enum' || field.source || (field.options || []).length > 0)
        .forEach((field) => {
          let group = groups.find((candidate) => candidate.name === field.group)

          if (!group) {
            group = { name: field.group, fields: [] }
            groups.push(group)
          }

          group.fields.push(field)
        })

      return groups
    },

    /**
     * What the form actually submits. Rows without a value are left out rather
     * than sent as empty: the server refuses a valueless filter (it would
     * silently widen the audience), and a half-filled row is a row the client
     * has not finished, not an error to shout about.
     */
    complete() {
      return this.rows
        // An unknown row has no `field`, so it cannot reach the payload - and
        // `unfinished` counts it, so the step cannot be submitted without it
        // being dealt with.
        .filter((row) => row.field && this.hasValue(row))
        .map((row) => ({ field: row.field, operator: row.operator, value: this.cleanValue(row) }))
    },

    /**
     * Rows the client started and did not finish. `complete` drops them from
     * the payload, which is right for the estimate but wrong for the step: a
     * filter that disappears on the way out is the one failure this feature
     * cannot have, so these block the step instead.
     */
    unfinished() {
      return this.rows.filter((row) => this.isUnfinished(row))
    },

    payload() {
      return JSON.stringify(this.complete)
    },

    /**
     * Whether a filter the step cannot render is on screen.
     *
     * Nothing downstream may run while one is: `complete` cannot carry it, so
     * the estimate and the creator list would both describe a WIDER audience
     * than the draft has - the same silent widening the row itself exists to
     * prevent, moved from the payload into the labels. Both are held back until
     * the client decides what to do with it.
     *
     * It can only ever start true and become false: an unknown row arrives with
     * a saved draft and the only thing that can be done with it is removal.
     */
    blocked() {
      return this.rows.some((row) => !!row.unknown)
    },

    /**
     * How many creators this campaign goes to as it stands.
     *
     * Picks win over the segment, and the sum is done here rather than asked
     * for: the checkbox and the label describing it are one thought, and a
     * network round trip between them shows up as the number lagging behind
     * the tick that changed it.
     *
     * Local arithmetic is exact because the selection is kept exact. Every id
     * in it either came from a list the segment produced, or survived the
     * pruning in fetchCount() - so counting them is counting recipients, not
     * guessing at them.
     */
    recipients() {
      return this.selected.length || this.count
    }
  },

  watch: {
    // No `deep`: `complete` builds a fresh array of fresh objects every time it
    // recomputes, so the reference alone already differs. Deep only added a
    // walk of the whole payload on every keystroke to reach the same answer.
    complete() {
      this.scheduleCount()

      // A different set of filters is a different list, so a selection made
      // against the old one no longer means anything. Clearing it is the
      // predictable move: keeping stale ids would show "3 picked" beside a
      // list none of them are in. The list itself reloads on the same change -
      // it watches the filters it was given.
      this.selected = []
    },

    // Removing the unrecognised row does not change `complete` - it was never
    // in it - so this is the only thing that can tell the step it may describe
    // its audience again.
    blocked(isBlocked) {
      if (!isBlocked) {
        this.scheduleCount()
      }
    },

    // Stops nagging the moment there is nothing left to nag about.
    unfinished(rows) {
      if (!rows.length) {
        this.showUnfinished = false
      }
    }
  },

  mounted() {
    this.scheduleCount()

    // The component owns the payload, so it owns whether the payload is fit to
    // send. The step's form is in the Blade around it; this is the only place
    // that knows a row is half-filled or unrecognised.
    this.form = this.$el.closest('form')

    if (this.form) {
      this.form.addEventListener('submit', this.guardStep)
    }
  },

  beforeUnmount() {
    if (this.form) {
      this.form.removeEventListener('submit', this.guardStep)
    }

    clearTimeout(this.countTimer)
    this.countRequest?.abort()
  },

  methods: {
    /**
     * A row the step cannot submit: one whose field this build does not know,
     * or one the client started and left without a value.
     *
     * Both block the step rather than being dropped from the payload. A filter
     * that disappears on the way out is the one failure this feature cannot
     * have - it does not narrow less, it sends to more people.
     */
    isUnfinished(row) {
      return !!row.unknown || (!!row.field && !this.hasValue(row))
    },

    guardStep(event) {
      if (!this.unfinished.length) {
        return
      }

      event.preventDefault()
      this.showUnfinished = true

      this.$nextTick(() => {
        const first = this.$el.querySelector('[data-unfinished] select, [data-unfinished] input')

        if (first) {
          first.focus()
        }
      })
    },

    fieldOf(row) {
      return row.field ? this.fields[row.field] : null
    },

    typeOf(row) {
      const field = this.fieldOf(row)

      return field ? field.type : null
    },

    optionsFor(row) {
      const field = this.fieldOf(row)

      return field && Array.isArray(field.options) ? field.options : []
    },

    operatorsFor(row) {
      const field = this.fieldOf(row)

      return field ? field.operators : []
    },

    /**
     * A name for one of a row's controls.
     *
     * The row reads as a sentence on screen - "Where Followers is at least 500"
     * - but a screen reader gets none of that layout: the three controls
     * announce as "combobox, combobox, combobox", identically on every row.
     * Naming each one after the filter it belongs to is what separates row two
     * from row five.
     */
    controlLabel(row, index, part) {
      const field = this.fieldOf(row)
      const subject = field ? field.label : `filter ${index + 1}`

      return part === 'Remove' ? `Remove ${subject}` : `${part} for ${subject}`
    },

    operatorLabel(operator, row) {
      const type = this.typeOf(row)

      if (type === 'date') {
        return { '>=': 'on or after', '<=': 'on or before', '>': 'after', '<': 'before' }[operator] || operator
      }

      if (type === 'bool') {
        return 'is'
      }

      return {
        in: 'is any of',
        not_in: 'is none of',
        contains: 'contains',
        not_contains: 'does not contain',
        '=': 'is',
        '!=': 'is not',
        '>': 'more than',
        '>=': 'at least',
        '<': 'less than',
        '<=': 'at most'
      }[operator] || operator
    },

    addRow() {
      this.rows.push(rowFrom(this.fields, null, this.nextKey++))
    },

    /**
     * Where a field's options come from, when not from the registry itself.
     * Only one does: a client's campaigns are unbounded, so they are paged from
     * an endpoint instead of being embedded in the page.
     */
    sourceOf(row) {
      const field = this.fieldOf(row)

      return field ? field.source || '' : ''
    },

    removeRow(index) {
      this.rows.splice(index, 1)
    },

    /**
     * Changing the field resets the operator and the value: an operator or a
     * value carried over from another field is almost never what was meant, and
     * would be rejected by the server anyway.
     */
    changeField(row, key) {
      const field = this.fields[key]

      row.field = key
      row.operator = field ? field.operators[0] : ''
      row.optionQuery = ''

      if (!field) {
        row.value = ''
        return
      }

      if (field.type === 'enum') {
        row.value = []
      } else if (field.type === 'bool') {
        row.value = true
      } else {
        row.value = ''
      }
    },

    hasValue(row) {
      const type = this.typeOf(row)

      if (type === 'enum') {
        return Array.isArray(row.value) && row.value.length > 0
      }

      if (type === 'bool') {
        return typeof row.value === 'boolean'
      }

      return row.value !== '' && row.value !== null && row.value !== undefined
    },

    /**
     * Free text with the "is any of" operator is a comma-separated list, which
     * is how a client naturally types "Texas, California".
     */
    cleanValue(row) {
      if (this.typeOf(row) === 'text' && row.operator === 'in') {
        return String(row.value).split(',').map((part) => part.trim()).filter(Boolean)
      }

      return row.value
    },

    scheduleCount() {
      clearTimeout(this.countTimer)
      this.countTimer = setTimeout(() => this.fetchCount(), LIST_DEBOUNCE)
    },

    async fetchCount() {
      // A number here would be an answer to a different question: the filters
      // the step can build, rather than the ones the draft holds.
      if (this.blocked) {
        this.count = null
        this.error = ''

        return
      }

      // Abandoned for the same reason the creator list abandons its own: the
      // debounce limits how many requests are sent, not the order they come
      // back in. A slow count for one set of filters landing after a fast one
      // for the next would leave the footer quoting a number for an audience
      // that is no longer on screen - and nothing about the page would look
      // wrong while it did.
      this.countRequest?.abort()
      this.countRequest = new AbortController()
      const request = this.countRequest

      this.isCounting = true
      this.error = ''

      // Remembered, so the answer can be matched to the question. Ticks made
      // while this is in the air are not in it and must not be judged by it.
      const sent = [...this.selected]

      try {
        const data = await requestJson(this.countUrl, 'POST', {
          audience_filters: this.complete,
          selected_creator_ids: sent
        }, request.signal)

        this.count = data.count
        this.prunePicks(sent, data.picked || [])
      } catch (e) {
        if (e.name === 'AbortError') {
          return
        }

        // The server is the authority on which filters are legal; its sentence
        // is written for the client, so it is the one shown.
        this.error = e.fromServer ? e.message : 'Could not reach the server.'
        this.count = null
      } finally {
        // Only the newest request owns the spinner.
        if (request === this.countRequest) {
          this.isCounting = false
        }
      }
    },

    /**
     * Drop the picks the segment no longer contains.
     *
     * Only ever needed for a selection restored from a saved draft: a creator
     * who unsubscribed since it was made is still in the stored list and is
     * not going to receive anything. Left in place, the footer would count them
     * and promise one more recipient than the send produces.
     *
     * Only what was actually asked about is dropped. A creator ticked while
     * the request was in flight was not in the question, so a missing answer
     * says nothing about them.
     *
     * @param {number[]} asked
     * @param {number[]} survived
     */
    prunePicks(asked, survived) {
      const alive = new Set(survived)
      const gone = new Set(asked.filter((id) => !alive.has(id)))

      if (gone.size) {
        this.selected = this.selected.filter((id) => !gone.has(id))
      }
    },

    /**
     * Anything past a handful of options is unusable as a wall of checkboxes -
     * content categories and a client's campaigns both are. Short, fixed lists
     * (gender, consent) keep the plain layout, where a search box would just be
     * another thing to skip past.
     */
    isSearchable(row) {
      return this.optionsFor(row).length > 6
    },

    visibleOptions(row) {
      const all = this.optionsFor(row)
      const query = (row.optionQuery || '').trim().toLowerCase()

      if (!query) return all

      const selected = Array.isArray(row.value) ? row.value : []

      // Already-ticked options stay visible whatever the search says: hiding a
      // choice the client has made reads as having lost it.
      return all.filter(function (option) {
        return selected.includes(option.value) || option.label.toLowerCase().includes(query)
      })
    }
  }
}
</script>
